import { z } from "zod";
import {
  CreateResult,
  DeleteParams,
  DeleteResult,
  GetListParams,
  GetListResult,
  GetOneParams,
  GetOneResult,
  UpdateParams,
  UpdateResult,
} from "react-admin";
import { getAuthenticatedServerClient, schemas } from "../api/server.api";
import { applyFilter, applyPagination, applySort } from "../backoffice.utils";
import { AbstractDataProvider } from "./AbstractDataProvider";
import { v4 as randomUuid } from "uuid";
import { DrivingLicenseClassEnum } from "../model/DrivingLicenseClass";

type CourseTemplateDto = z.infer<typeof schemas.CourseTemplateDto>;
type CreateCourseTemplateDto = z.infer<typeof schemas.CreateCourseTemplateDto>;
type UpdateCourseTemplateDto = z.infer<typeof schemas.UpdateCourseTemplateDto>;

const _CourseModuleSchema = z.object({
  name: z.string(),
  color: z.string(),
  ue: z.number().int().min(1),
});
export type CourseModule = z.output<typeof _CourseModuleSchema>;

const _CourseTemplateSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  color: z.string(),
  drivingSchoolId: z.string(),
  drivingLicenseClasses: z.array(DrivingLicenseClassEnum),
  modules: z.array(_CourseModuleSchema).min(1),
});
export type CourseTemplate = z.output<typeof _CourseTemplateSchema>;

class CourseTemplatesProvider extends AbstractDataProvider<CourseTemplate> {
  async getOne(_: string, { id }: GetOneParams<CourseTemplate>): Promise<GetOneResult<CourseTemplate>> {
    const serverClient = await getAuthenticatedServerClient();
    const dto = await serverClient.getCourseTemplate({ params: { id } });
    const record = _dtoToRecord(dto);
    return { data: record };
  }

  async getList(_resource: string, params: GetListParams): Promise<GetListResult<CourseTemplate>> {
    const { filter, sort, pagination } = params;
    const { drivingSchoolId, ...clientSideFilter } = filter;
    if (!drivingSchoolId) {
      throw new Error(`Unexpected filter: ${JSON.stringify(filter)} -- expected: {"drivingSchoolId": ...}`);
    }
    const serverClient = await getAuthenticatedServerClient();
    const dtos = await serverClient.findCourseTemplates({ queries: { drivingSchoolId } });
    const records = dtos.map(_dtoToRecord);
    return applyPagination(applySort(applyFilter(records, clientSideFilter), sort), pagination);
  }

  async create(_resource: string, params: { data: Omit<CourseTemplate, "id"> }): Promise<CreateResult<CourseTemplate>> {
    const { data } = params;
    const serverClient = await getAuthenticatedServerClient();
    const createDto: CreateCourseTemplateDto = {
      ...data,
      idempotencyKey: randomUuid(),
    };
    const dto = await serverClient.createCourseTemplate(createDto);
    const record = _dtoToRecord(dto);
    return { data: record };
  }

  async update(_resource: string, params: UpdateParams<CourseTemplate>): Promise<UpdateResult<CourseTemplate>> {
    const { id, data } = params;
    const serverClient = await getAuthenticatedServerClient();
    const updateDto: UpdateCourseTemplateDto = data;
    const dto = await serverClient.updateCourseTemplate(updateDto, { params: { id } });
    const record = _dtoToRecord(dto);
    return { data: record };
  }

  async delete(_resource: string, params: DeleteParams): Promise<DeleteResult> {
    const { id } = params;
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.deleteCourseTemplate(undefined, { params: { id } });
    return { data: {} };
  }
}

export const courseTemplatesProvider = new CourseTemplatesProvider();

function _dtoToRecord(dto: CourseTemplateDto): CourseTemplate {
  return _CourseTemplateSchema.parse(dto);
}
